import React from 'react'
import { useHistory } from 'react-router-dom'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { Tooltip, Checkbox, Button, Slide, Paper, IconButton } from '@material-ui/core'
import { LanguageConsumer } from '../../context/LanguageContext'
import { ListingConsumer } from '../../context/ListingContext'
import { FormLoader } from '../Loader/FormLoader'
import GeneralTableWithFilter from '../GeneralTableWithFilter'
import { useStyles } from './Styles'
import 'moment/locale/ar-sa'
import Moment from 'react-moment'
import { NamedRoutes } from '../../routes'
import invoicesTypes from '../../configurations/invoices-types'
import AddNewForm from '../../FormFields/EntityAddForm'
import AddNewDialog from '../Dialog/AddNewDialog'
import DraftsActionsMenu from './ActionsMenu'
import PostAddIcon from '@material-ui/icons/PostAdd'
import DeleteForeverOutlinedIcon from '@material-ui/icons/DeleteForeverOutlined'

const TemplatesList = ({ listingData, locale, openDialog }) => {
  const classes = useStyles()
  let history = useHistory()
  const intl = useIntl()
  const [selectedRows, setSelectedRows] = React.useState([])

  const clearSelectedRows = () => {
    setSelectedRows([])
  }

  const updateSelectedRows = (requestNumbersToRemove) => {
    setSelectedRows([...(selectedRows.filter(item => !requestNumbersToRemove.includes(item.id)))])
  }

  const columns = [
    {
      name: 'id',
      key_name: 'id',
      label: intl.formatMessage({ id: 'Fields.label.view.id' }),
      options: {
        filter: false,
        viewColumns: false,
        display: 'false',
        print: false,
        sort: false,
        download: false
      }
    },
    {
      name: 'bulk_action',
      key_name: 'bulk_action',
      options: {
        filter: false,
        print: false,
        sort: false,
        // display: !!hasBulkActions ? 'true' : 'false',
        display: 'true',
        viewColumns: false,
        download: false,
        customHeadLabelRender: (columnMeta) =>
          <Tooltip title={intl.formatMessage({ id: 'selectAll.title' })}>
            <span>
              <Checkbox
                checked={!!listingData.length && selectedRows.length === listingData.length}
                onChange={(event) => {
                  setSelectedRows(prevState => !!event.target.checked ? [...new Set([...prevState, ...listingData])] :
                    prevState.filter(({ id: template_id1 }) => !listingData.some(({ id: template_id2 }) => template_id1 === template_id2)))
                }}
                indeterminate={selectedRows.length > 0 && selectedRows.length !== listingData.length}
                color="secondary"
              />
            </span>
          </Tooltip>,
        customBodyRenderLite: (dataIndex) => {
          let template_id = listingData[dataIndex]?.id
          return <Checkbox
            checked={!!selectedRows.length && !!selectedRows.filter(template => template.id === template_id).length}
            // disabled={!getBulkCheckboxDisabled(listingData[dataIndex])}
            onChange={(event) => {
              setSelectedRows(prevState => prevState.filter(template => template.id === template_id).length
                ? prevState.filter(template => template.id !== template_id)
                : [...prevState, listingData[dataIndex]])
            }}
            color="secondary"
          />
        }
      }
    },
    {
      name: 'name',
      key_name: 'name',
      label: intl.formatMessage({ id: 'Fields.label.view.templateNamePrivilege' }),
      options: {
        filter: false,
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let name = listingData[dataIndex]?.name || ''
          let privilege = listingData[dataIndex]?.privilegeId || null
          return <div className={classes.cellDataWrapper}>
            <strong className={'noWrap'}>{!!name ? name : '—'}</strong>
            {!!privilege &&
            <span className={'noWrap'}><FormattedMessage id={`Fields.label.view.privilege.${privilege}`}/></span>}
          </div>
        }
      }
    },
    {
      name: 'updatedAt',
      key_name: 'updatedAt',
      label: intl.formatMessage({ id: 'Fields.label.view.updatedAt' }),
      options: {
        filter: false,
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let value = listingData[dataIndex]?.updatedAt
          return !!value &&
            <div className={classes.cellDataWrapper}>
              <strong><Moment className={'noWrap'} locale={locale === 'ar' ? 'ar-sa' : 'en-us'}
                              format={locale === 'ar' ? 'YYYY/MM/DD' : 'DD/MM/YYYY'}>{value}</Moment></strong>
              <span><Moment className={'noWrap'} locale={locale === 'ar' ? 'ar-sa' : 'en-us'}
                            format="hh:mm A">{value}</Moment></span>
            </div>
        }
      }
    },
    {
      name: 'typeName',
      key_name: 'documentType',
      label: intl.formatMessage({ id: 'Fields.label.view.typeAndVersion' }),
      options: {
        filter: false,
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let type = invoicesTypes?.filter(item => item.key.toString().toLowerCase() === listingData[dataIndex]?.document?.documentType?.toString()?.toLowerCase())
          return !!type && !!type.length &&
            <div className={classes.cellDataWrapper}>
              <strong className={'noWrap'}>{type[0][`name_${locale}`]}</strong>
              <span className={'noWrap'}><FormattedMessage
                id='Fields.label.view.version'/>: {listingData[dataIndex]?.document?.documentTypeVersion}</span>
            </div>
        }
      }
    },
    {
      name: 'total',
      key_name: 'total',
      label: intl.formatMessage({ id: 'Fields.label.view.total' }),
      options: {
        filter: false,
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          return <div className={classes.totalWrapper}>
            <strong><FormattedNumber value={listingData[dataIndex]?.document?.totalAmount || 0}/> </strong>
            <FormattedMessage id='EGP'/>
          </div>
        }
      }
    },
    {
      name: 'totalSales',
      key_name: 'totalSales',
      label: intl.formatMessage({ id: 'Fields.label.view.totalSales' }),
      options: {
        filter: false,
        display: 'false',
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          return <div className={classes.totalWrapper}>
            <strong><FormattedNumber value={listingData[dataIndex]?.document?.totalSalesAmount || 0}/> </strong>
            <FormattedMessage
              id='EGP'/>
          </div>
        }
      }
    },
    {
      name: 'totalDiscount',
      key_name: 'totalDiscount',
      label: intl.formatMessage({ id: 'Fields.label.view.totalDiscount' }),
      options: {
        filter: false,
        display: 'false',
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          return <div className={classes.totalWrapper}>
            <strong><FormattedNumber value={listingData[dataIndex]?.document?.totalDiscountAmount || 0}/> </strong>
            <FormattedMessage
              id='EGP'/>
          </div>
        }
      }
    },
    {
      name: 'netAmount',
      key_name: 'netAmount',
      label: intl.formatMessage({ id: 'Fields.label.view.netAmount' }),
      options: {
        filter: false,
        display: 'false',
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          return <div className={classes.totalWrapper}>
            <strong><FormattedNumber value={listingData[dataIndex]?.document?.netAmount || 0}/> </strong>
            <FormattedMessage
              id='EGP'/>
          </div>
        }
      }
    },
    {
      name: 'issuerName',
      key_name: 'issuerName',
      label: intl.formatMessage({ id: 'Fields.label.view.issuerName' }),
      options: {
        display: 'false',
        filter: false,
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          return !!listingData[dataIndex]?.document?.issuer?.name &&
            <div className={classes.cellDataWrapper}>
              <strong>{listingData[dataIndex]?.document?.issuer?.name}</strong>
              {!!listingData[dataIndex]?.trn && <span>{listingData[dataIndex]?.trn}</span>}
            </div>
        }
      }
    },
    {
      name: 'receiverName',
      key_name: 'receiverName',
      label: intl.formatMessage({ id: 'Fields.label.view.receiverName' }),
      options: {
        filter: false,
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          return !!listingData[dataIndex]?.document?.receiver?.name &&
            <div className={classes.cellDataWrapper}>
              <strong>{listingData[dataIndex]?.document?.receiver?.name}</strong>
              {!!listingData[dataIndex]?.document?.receiver?.id &&
              <span>{listingData[dataIndex]?.document?.receiver?.id}</span>}
            </div>
        }
      }
    },
    {
      name: 'status',
      key_name: 'status',
      label: intl.formatMessage({ id: 'Fields.label.view.Status' }),
      options: {
        filter: false,
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let value = listingData[dataIndex]?.status
          return <span className={classes.status + ((value === 'READY') ? ' active' : ' not-active')}>{value}</span>
        }
      }
    },
    {
      name: 'createdAt',
      key_name: 'createdAt',
      label: intl.formatMessage({ id: 'Fields.label.view.createdAt' }),
      options: {
        filter: false,
        display: 'false',
        print: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          let value = listingData[dataIndex]?.createdAt
          return !!value &&
            <div className={classes.cellDataWrapper}>
              <strong><Moment className={'noWrap'} locale={locale === 'ar' ? 'ar-sa' : 'en-us'}
                              format={locale === 'ar' ? 'YYYY/MM/DD' : 'DD/MM/YYYY'}>{value}</Moment></strong>
              <span><Moment className={'noWrap'} locale={locale === 'ar' ? 'ar-sa' : 'en-us'}
                            format="hh:mm A">{value}</Moment></span>
            </div>
        }
      }
    },
    {
      name: '',
      options: {
        filter: false,
        print: false,
        sort: false,
        viewColumns: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          return <div className={classes.actionsWrapper}>
            <Tooltip
              title={intl.formatMessage({ id: 'CreateInvoiceFromTemplate' }, { name: listingData[dataIndex]?.name })}
              arrow>
              <IconButton size={'small'} color={'primary'} className={classes.invoiceTemplateCreate}
                          onClick={() => history.push({
                            pathname: NamedRoutes.newInvoice,
                            search: `?templateId=${listingData[dataIndex]?.id}`
                          })}
                          onMouseDown={() => history.push({
                            pathname: NamedRoutes.newInvoice,
                            search: `?templateId=${listingData[dataIndex]?.id}`
                          })}>
                <PostAddIcon/>
              </IconButton>
            </Tooltip>
            <DraftsActionsMenu selectedRow={listingData[dataIndex]}
                               openDialog={openDialog}
                               isTemplate
                               id={listingData[dataIndex]?.id}
                               locale={locale}/>
          </div>
        }
      }
    }
  ]

  return (
    <>
      <LanguageConsumer>
        {({ locale }) => (
          <ListingConsumer>
            {({
                listingCount, listingRowsPerPage, listingPage, handleChangeListingPage, handleChangeListingRowsPerPage,
                handleListingFilterSubmit, listingLoading,
                entityName, currentDialogAction, currentSelectedRow, dialogActionSubmit, cancelDialog, isDialogOpen
              }) => (
              <div className={'listing-wrapper'}>
                <AddNewDialog
                  children={
                    <AddNewForm
                      entityName={entityName}
                      currentData={currentSelectedRow}
                      currentBulkData={selectedRows}
                      onSubmit={dialogActionSubmit}
                      updateSelectedRows={updateSelectedRows}
                      currentAction={currentDialogAction}
                      updateParentEntity={cancelDialog}
                      cancelDialog={() => cancelDialog()}/>}
                  dialogAction={currentDialogAction}
                  DialogTitle={<FormattedMessage id={`invoiceTemplate.dialogTitle.${currentDialogAction}`}
                                                 values={{ id: currentSelectedRow?.name }}/>}
                  isDialogOpen={isDialogOpen}
                  cancelDialog={() => cancelDialog()}
                />
                <GeneralTableWithFilter
                  columns={columns}
                  type={'Templates'}
                  noFilter
                  listingData={listingData}
                  listingCount={listingCount}
                  listingRowsPerPage={listingRowsPerPage}
                  listingPage={listingPage}
                  handleChangeListingPage={handleChangeListingPage}
                  handleChangeListingRowsPerPage={handleChangeListingRowsPerPage}
                  handleListingFilterSubmit={handleListingFilterSubmit}
                  locale={locale}
                  titleKey={'templatesPageContent.PageTitle'}
                />
                {!!listingLoading && <FormLoader loading={true}/>}
              </div>
            )}
          </ListingConsumer>
        )}
      </LanguageConsumer>

      <Slide direction="up" in={!!selectedRows.length} mountOnEnter unmountOnExit>
        <Paper elevation={4} className={classes.BulkActionsWrapper}>
          <div className={classes.BulkActionsCountWrapper + ' ' + classes.BulkActionsMessage}>
            <p><FormattedMessage id='templatesSelect.AllCount'
                                 values={{
                                   strong: chunks => <strong>{chunks}</strong>,
                                   count: selectedRows.length
                                 }}/></p>
          </div>
          <div className={classes.BulkActionsCountWrapper}>
            <Button size={'medium'} variant="contained" color="secondary"
                    className={classes.bulkClearBtn}
                    startIcon={<DeleteForeverOutlinedIcon/>}
                    onClick={() => openDialog('bulk-delete', selectedRows)}>
              <FormattedMessage id='Drafts.BulkAction.actions.bulk-delete'/>
            </Button>
            <Button size={'medium'} variant="outlined" color="secondary"
                    className={classes.bulkClearBtn}
                    onClick={clearSelectedRows}>
              <FormattedMessage id='BulkAction.actions.clear'/>
            </Button>
          </div>
        </Paper>
      </Slide>

    </>
  )
}

export default TemplatesList
